import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, Alert, Table, Tag, Button, Popconfirm, message, Space, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import styled from "styled-components";
import customClientAxios from "../customAxios";
import * as Api from "../utils/ApiEndpoints";

const PageWrapper = styled.div`
  padding: 28px 32px;
  animation: fadeIn 0.3s ease;
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
`;
const PageHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 24px; flex-wrap: wrap; gap: 16px;
`;
const PageTitle = styled.h1`
  font-size: 26px; font-weight: 600; color: #1e293b; letter-spacing: -0.5px; margin: 0;
`;
const PageSubtitle = styled.p`font-size: 14px; color: #64748b; margin: 4px 0 0;`;
const TableCard = styled.div`
  background: white; border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  border: 1px solid #e2e8f0; overflow: hidden;
`;

export default function CategoryPagesList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(Api.CategoryPagesList);
      setRows(res.data?.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    try {
      await customClientAxios().delete(Api.CategoryPageDetail.replace(":id", id));
      message.success("Deleted");
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to delete");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (v, row) => (
        <div>
          <div style={{ fontWeight: 600, color: "#0f172a" }}>{v}</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>/category/{row.slug}</div>
        </div>
      ),
    },
    { title: "Status", dataIndex: "status", render: (v) => <Tag color={v === "published" ? "green" : "default"}>{v}</Tag> },
    { title: "Events", dataIndex: "eventCount", align: "right" },
    {
      title: "Category views (unique / total)",
      key: "pageViews",
      align: "right",
      render: (_, r) => `${r.pageViews?.unique || 0} / ${r.pageViews?.total || 0}`,
    },
    {
      title: "Clicks (unique / total)",
      key: "cardClicks",
      align: "right",
      render: (_, r) => `${r.cardClicks?.unique || 0} / ${r.cardClicks?.total || 0}`,
    },
    { title: "Sales", dataIndex: "totalSales", align: "right" },
    {
      title: "Stats refreshed",
      dataIndex: "lastRefreshedAt",
      render: (v) => v ? new Date(v).toLocaleString() : <span style={{ color: "#94a3b8" }}>Never</span>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, row) => (
        <Space>
          <Tooltip title="View / manage">
            <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/category-pages/${row._id}`)} />
          </Tooltip>
          <Tooltip title="Edit details">
            <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/category-pages/${row._id}/edit`)} />
          </Tooltip>
          <Popconfirm title="Delete this category page?" description="This cannot be undone." onConfirm={() => onDelete(row._id)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageWrapper>
      <PageHeader>
        <div>
          <PageTitle>Category Pages</PageTitle>
          <PageSubtitle>Curated public pages featuring events from across Growezy.</PageSubtitle>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/category-pages/new")}>
            New Category Page
          </Button>
        </Space>
      </PageHeader>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <TableCard>
        {loading ? (
          <div style={{ padding: 80, textAlign: "center" }}><Spin /></div>
        ) : (
          <Table
            rowKey="_id"
            dataSource={rows}
            columns={columns}
            pagination={{ pageSize: 20, showSizeChanger: false }}
          />
        )}
      </TableCard>
    </PageWrapper>
  );
}
